/* Shared follow-up logic for the leads pipeline. Source of truth for both
 * the table filter dropdown and the sidebar red-dot indicator so they can
 * never disagree about what "needs follow-up" means.
 */

// Treat a lead as "stale" when the most recent call_log is older than this.
// Mirrors the threshold used server-side in /api/leads/follow-up-count.
export const STALE_MS = 7 * 24 * 60 * 60 * 1000;

/** True when this lead should appear in the "needs follow-up" bucket
 *  (never called OR called > 7 days ago).
 *
 *  Reads `Date.now()` at call-time so the answer always reflects the moment
 *  the user is looking at the screen — no stale memoization.
 */
export function needsFollowUp(lead) {
  if (!lead) return false;
  const last = lead.last_call;
  if (!last) return true;
  return (Date.now() - new Date(last.created_at).getTime()) > STALE_MS;
}

/** Predicate for the leads-table follow-up filter dropdown.
 *  Modes: "all" | "needs" | "stale" | "never".
 */
export function matchesFollowUp(lead, mode) {
  if (mode === "all") return true;
  const last = lead?.last_call;
  const isStale = last && (Date.now() - new Date(last.created_at).getTime()) > STALE_MS;
  if (mode === "never") return !last;
  if (mode === "stale") return !!isStale;
  if (mode === "needs") return !last || isStale;
  return true;
}

/** Compact "X ago" formatter used in the LastContactBadge. */
export function shortTimeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)        return "just now";
  if (diff < 3600)      return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400*30)  return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
